import { clsx } from 'clsx';

import { Tooltip, Typography } from '@/ui';

import type { PulseReportDelta } from './deltaTypes';
import type { TypographyProps } from '@mui/material/Typography';
import type { FC } from 'react';

interface AnswerDeltaProps {
  className?: string;
  delta: PulseReportDelta;
  variant?: TypographyProps['variant'];
}

const formatDelta = (delta: number) => {
  if (delta > 0) {
    return `+${delta}`;
  }
  return `${delta}`;
};

const MONTH_BY_INDEX = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const formatTooltipDate = (submittedAt: string) => {
  const date = new Date(submittedAt);
  if (Number.isNaN(date.valueOf())) {
    return submittedAt;
  }
  return `${MONTH_BY_INDEX[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const AnswerDelta: FC<AnswerDeltaProps> = ({
  className,
  delta,
  variant = 'subtitle1',
}) => {
  const {
    delta: deltaValue,
    previousMax,
    previousSubmittedAt,
    previousValue,
  } = delta;
  let deltaKind: 'positive' | 'negative' | 'neutral' = 'neutral';
  if (deltaValue > 0) {
    deltaKind = 'positive';
  }
  if (deltaValue < 0) {
    deltaKind = 'negative';
  }
  const previousResultLabel = `Previous Result: ${previousValue}/${previousMax}`;
  const previousDateLabel = `on ${formatTooltipDate(previousSubmittedAt)}`;

  return (
    <Tooltip
      placement="top"
      title={
        <>
          <span>{previousResultLabel}</span>
          <br />
          <span>{previousDateLabel}</span>
        </>
      }
    >
      <span className="inline-flex">
        <Typography
          className={clsx(
            'whitespace-nowrap font-medium leading-none',
            {
              'text-fire-500': deltaValue < 0,
              'text-sage-500': deltaValue > 0,
              'text-sunny-500': deltaValue === 0,
            },
            className,
          )}
          data-delta-kind={deltaKind}
          variant={variant}
        >
          {formatDelta(deltaValue)}
        </Typography>
      </span>
    </Tooltip>
  );
};

export default AnswerDelta;
